/** Account roles, from least to most privileged. */
export const ROLES = ["general", "admin", "developer"] as const;
export type Role = (typeof ROLES)[number];

/** Actions guarded by role. Keep this list small; add actions, not special cases. */
export type Action =
  | "content:read"
  | "content:write"
  | "reports:read"
  | "reports:resolve"
  | "users:read"
  | "users:set-role"
  | "audit:read";

const PERMISSIONS: Record<Role, readonly Action[]> = {
  general: ["content:read"],
  admin: [
    "content:read",
    "content:write",
    "reports:read",
    "reports:resolve",
    "users:read",
    "audit:read",
  ],
  developer: [
    "content:read",
    "content:write",
    "reports:read",
    "reports:resolve",
    "users:read",
    "users:set-role",
    "audit:read",
  ],
};

export function isRole(value: unknown): value is Role {
  return typeof value === "string" && (ROLES as readonly string[]).includes(value);
}

/** True when `role` may perform `action`. Unknown roles get nothing. */
export function can(role: string | null | undefined, action: Action): boolean {
  return isRole(role) && PERMISSIONS[role].includes(action);
}

/** True for roles that may open the admin area. */
export function isStaff(role: string | null | undefined): boolean {
  return can(role, "content:write");
}

export type RoleChangeResult = { ok: true } | { ok: false; reason: RoleChangeDenial };
export type RoleChangeDenial = "forbidden" | "invalid_role" | "last_developer" | "no_change";

/**
 * Decide whether `actorRole` may change a user from `currentRole` to `nextRole`.
 * Only developers may change roles, and the last developer can never be demoted.
 */
export function checkRoleChange(input: {
  actorRole: string;
  currentRole: Role;
  nextRole: string;
  developerCount: number;
}): RoleChangeResult {
  if (!can(input.actorRole, "users:set-role")) return { ok: false, reason: "forbidden" };
  if (!isRole(input.nextRole)) return { ok: false, reason: "invalid_role" };
  if (input.currentRole === input.nextRole) return { ok: false, reason: "no_change" };
  if (input.currentRole === "developer" && input.developerCount <= 1) {
    return { ok: false, reason: "last_developer" };
  }
  return { ok: true };
}
